import React, { useState, useEffect, useRef } from 'react';
import {
  getSoundFXEnabled,
  toggleSoundFX,
  subscribeSoundFX,
  playHoverSound,
  playClickSound,
  playSurfingSound,
  initAudio,
} from '../utils/audio';

export default function SoundControl() {
  const [isEnabled, setIsEnabled] = useState(getSoundFXEnabled());
  const [isVisible, setIsVisible] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  // Mouse surfing and hover tracking
  const mousePosRef = useRef({ x: 0, y: 0, lastCheck: Date.now(), totalDist: 0 });
  const lastHoveredElementRef = useRef(null);

  // Sync state with global audio manager
  useEffect(() => {
    return subscribeSoundFX((active) => {
      setIsEnabled(active);
    });
  }, []);

  // Show button only when scrolling past the hero section (~450px or hero height)
  useEffect(() => {
    const handleScroll = () => {
      const heroEl = document.getElementById('hero');
      const heroBottom = heroEl ? heroEl.offsetHeight * 0.75 : 450;
      setIsVisible(window.scrollY > heroBottom);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll(); // Initial check

    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Handle first user gesture to unlock audio context smoothly & handle visibility change
  useEffect(() => {
    const handleFirstGesture = () => {
      if (getSoundFXEnabled()) {
        initAudio();
      }
      window.removeEventListener('pointerdown', handleFirstGesture);
      window.removeEventListener('keydown', handleFirstGesture);
    };

    window.addEventListener('pointerdown', handleFirstGesture, { once: true });
    window.addEventListener('keydown', handleFirstGesture, { once: true });

    return () => {
      window.removeEventListener('pointerdown', handleFirstGesture);
      window.removeEventListener('keydown', handleFirstGesture);
    };
  }, []);

  // Global mouse surfing, hover (on all elements), and click interaction listeners
  useEffect(() => {
    // 1. Mouse surfing listener (calm breath of air / harmonic shimmer on continuous smooth exploration)
    const handleMouseMove = (e) => {
      const now = Date.now();
      const prev = mousePosRef.current;
      const dx = e.clientX - prev.x;
      const dy = e.clientY - prev.y;
      const dist = Math.sqrt(dx * dx + dy * dy);

      prev.x = e.clientX;
      prev.y = e.clientY;

      // Accumulate distance over time window
      if (now - prev.lastCheck > 900) {
        prev.totalDist = 0;
        prev.lastCheck = now;
      }

      prev.totalDist += dist;

      // If user is exploring smoothly across sections
      if (prev.totalDist > 360) {
        prev.totalDist = 0;
        prev.lastCheck = now;
        playSurfingSound(Math.min(1.8, dist / 22));
      }
    };

    // 2. Hover listener for ALL elements across the site (cards, buttons, text, images, headings, etc.)
    const handlePointerOver = (e) => {
      const target = e.target;
      if (!target || !(target instanceof Element)) return;

      // Skip triggering on the html/body root container itself
      if (target === document.documentElement || target === document.body) return;

      // Find the most meaningful element under the pointer
      const meaningfulElement = target.closest(
        'a, button, [role="button"], input, textarea, select, summary, [data-interactive="true"], .cursor-pointer, h1, h2, h3, h4, h5, h6, p, img, [role="img"], li, blockquote, figure, article, section, [class*="card"], [class*="box"], [class*="badge"], [class*="border"], svg, i, span, div'
      ) || target;

      // Trigger if crossing into a new distinct element
      if (meaningfulElement && meaningfulElement !== lastHoveredElementRef.current) {
        lastHoveredElementRef.current = meaningfulElement;
        playHoverSound();
      }
    };

    // 3. Click listener (tactile wooden cork & copper feedback)
    const handlePointerDown = (e) => {
      initAudio();

      const target = e.target;
      if (!target || !(target instanceof Element)) return;

      // Play tactile click sound on all clicks across the site
      playClickSound();
    };

    window.addEventListener('mousemove', handleMouseMove, { passive: true });
    document.addEventListener('pointerover', handlePointerOver, { passive: true });
    document.addEventListener('pointerdown', handlePointerDown, { passive: true });

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('pointerover', handlePointerOver);
      document.removeEventListener('pointerdown', handlePointerDown);
    };
  }, []);

  const handleToggle = (e) => {
    e.stopPropagation();
    initAudio();
    toggleSoundFX();
  };

  return (
    <aside
      aria-label="Lydatmosfære kontrol"
      className={`fixed bottom-4 right-4 sm:bottom-6 sm:right-6 z-50 transition-all duration-500 ease-out transform ${
        isVisible
          ? 'opacity-100 translate-y-0 pointer-events-auto scale-100'
          : 'opacity-0 translate-y-8 pointer-events-none scale-95'
      }`}
    >
      <button
        type="button"
        onClick={handleToggle}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        aria-label={isEnabled ? 'Slå lydatmosfære fra' : 'Slå lydatmosfære til'}
        aria-pressed={isEnabled}
        className={`group relative flex items-center gap-3 px-4 py-2.5 border backdrop-blur-md transition-all duration-300 cursor-pointer shadow-[0_4px_20px_rgba(55,51,47,0.08)] focus:outline-none ${
          isEnabled
            ? 'bg-[#F6F2EB] border-[#C99850] text-[#37332F]'
            : 'bg-[#F6F2EB]/90 border-[#DED7CC] hover:border-[#37332F]/40 text-[#5E5852]'
        }`}
      >
        {/* Animated Sound Waves Visualizer */}
        <div className="flex items-center gap-[3px] h-4 w-4.5 justify-center" aria-hidden="true">
          {isEnabled ? (
            <>
              <span className="w-[2px] bg-[#C99850] animate-wave-1" />
              <span className="w-[2px] bg-[#37332F] animate-wave-2" />
              <span className="w-[2px] bg-[#C99850] animate-wave-3" />
              <span className="w-[2px] bg-[#37332F] animate-wave-4" />
            </>
          ) : (
            <div className="flex items-center gap-[3px] opacity-40">
              <span className="w-[2px] h-1.5 bg-[#8E867E]" />
              <span className="w-[2px] h-2 bg-[#8E867E]" />
              <span className="w-[2px] h-1.5 bg-[#8E867E]" />
              <span className="w-[2px] h-1 bg-[#8E867E]" />
            </div>
          )}
        </div>

        {/* Label with Scandinavian Typography */}
        <div className="flex flex-col text-left">
          <span className="font-['Montserrat'] text-[10px] uppercase tracking-[0.2em] font-medium text-[#37332F] leading-tight">
            {isEnabled ? 'Lyd Til' : 'Lyd Fra'}
          </span>
          <span className="text-[8.5px] tracking-[0.14em] uppercase font-light text-[#8E867E]">
            {isEnabled ? 'Atmosfære' : 'Slået fra'}
          </span>
        </div>

        {/* Subtle status dot */}
        <span
          className={`w-1.5 h-1.5 transition-all duration-300 ${
            isEnabled
              ? 'bg-[#C99850]'
              : 'bg-[#DED7CC]'
          }`}
          aria-hidden="true"
        />
      </button>
    </aside>
  );
}
